import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination,
  Box,
  Typography,
  Checkbox,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
} from "@material-ui/core";
import { ReceiptOutlined, Payment as PaymentIcon } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { useNavigate } from "react-router-dom";
import Scrollbar from "../../Scrollbar";
import FacturaRow from "./FacturaRow.js";

import { useClienteFilters } from "src/components/cliente/hooks/useClienteFilters";
import ClienteFilterPanel from "src/components/cliente/filters/ClienteFilterPanel";
import TableSkeleton from "src/components/cliente/loading/TableSkeleton";
import EmptyState from "src/components/cliente/empty/EmptyState";
import { TablaHead } from "src/components/_dashboard/tablas";
import { formatAsCLP } from "src/utils/formatNumber";
import { useFacturasMultisociedad } from "./hooks/useFacturasMultisociedad";

// Columnas unificadas (planes + tareas)
// hiddenOnMobile: columnas que se ocultan en pantallas pequeñas.
// La info queda accesible desde el modal "Ver detalle".
const TABLE_HEAD = [
  { id: "checkbox", label: "", alignRight: false, sortable: false },
  {
    id: "tipoFactura",
    label: "Tipo",
    alignRight: false,
    hiddenOnMobile: true,
  },
  { id: "folio", label: "Folio", alignRight: false },
  {
    id: "nombreSociedad",
    label: "Sociedad",
    alignRight: false,
    hiddenOnMobile: true,
  },
  {
    id: "detalleFactura",
    label: "Glosa",
    alignRight: false,
    hiddenOnMobile: true,
  },
  {
    id: "fechaFacturacion",
    label: "Fecha Facturación",
    alignRight: false,
    hiddenOnMobile: true,
  },
  { id: "fecha", label: "Periodo", alignRight: false, hiddenOnMobile: true },
  { id: "montoVisible", label: "Monto", alignRight: true },
  { id: "estadoFactura", label: "Estado", alignRight: false },
  {
    id: "fechaPago",
    label: "Fecha Pago",
    alignRight: false,
    hiddenOnMobile: true,
  },
  {
    id: "acciones",
    label: "Acciones",
    alignRight: false,
    sortable: false,
  },
];

export default function TablaFacturasCliente() {
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // En mobile se muestran solo las columnas críticas
  const visibleHeads = isMobile
    ? TABLE_HEAD.filter((col) => !col.hiddenOnMobile)
    : TABLE_HEAD;

  // Toggle para mostrar/ocultar anuladas
  const [mostrarAnuladas, setMostrarAnuladas] = useState(false);

  // Estado de selección para pago múltiple
  const [seleccionadas, setSeleccionadas] = useState([]);

  const montoTotal = useMemo(
    () => seleccionadas.reduce((acc, f) => acc + Number(f.montoAPagar), 0),
    [seleccionadas],
  );

  // Hook de filtros
  const {
    filtros,
    updateFiltro,
    toggleEstado,
    limpiarFiltros,
    removerFiltro,
    filtrosActivos,
    hayFiltrosActivos,
  } = useClienteFilters("facturas");

  // Estados para tabla
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("desc");
  // Sin columna explícita el backend conserva la prioridad de estados vigente.
  const [orderBy, setOrderBy] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rutSociedad, setRutSociedad] = useState(null);

  const {
    sociedades,
    facturas: facturasApi,
    paginacion,
    resumen: totales,
    isLoading,
    isFetching,
    isError,
    isForbidden,
  } = useFacturasMultisociedad({
    filtros,
    mostrarAnuladas,
    order,
    orderBy,
    page,
    rowsPerPage,
    rutSociedad,
  });

  useEffect(() => {
    if (rutSociedad === null && sociedades.length > 0) {
      setRutSociedad("");
    }
  }, [rutSociedad, sociedades]);

  useEffect(() => {
    setPage(0);
    setSeleccionadas([]);
  }, [
    filtros.busqueda,
    filtros.estados,
    filtros.fechaFacturacionDesde,
    filtros.fechaFacturacionHasta,
    filtros.montoMax,
    filtros.montoMin,
    filtros.tipoFactura,
    mostrarAnuladas,
  ]);

  const facturasFiltradas = useMemo(
    () =>
      facturasApi.map((factura) => ({
        ...factura,
        tipo: factura.tipoFactura,
        total: factura.montoOriginal,
        montoAPagar: factura.montoVisible,
      })),
    [facturasApi],
  );

  // Handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ── Handlers de selección múltiple ─────────────────────────────────────
  const puedenPagarse = useMemo(
    () =>
      facturasFiltradas.filter((f) =>
        ["FACTURADO", "ABONO"].includes(f.estadoFactura),
      ),
    [facturasFiltradas],
  );

  const estaSeleccionada = useCallback(
    (factura) =>
      seleccionadas.some((f) => f.id === factura.id && f.tipo === factura.tipo),
    [seleccionadas],
  );

  const handleToggle = useCallback((factura) => {
    setSeleccionadas((prev) => {
      const ya = prev.some(
        (f) => f.id === factura.id && f.tipo === factura.tipo,
      );
      return ya
        ? prev.filter((f) => !(f.id === factura.id && f.tipo === factura.tipo))
        : [...prev, factura];
    });
  }, []);

  const handleToggleAll = () => {
    setSeleccionadas((prev) => {
      const clavesPagina = new Set(
        puedenPagarse.map((f) => `${f.tipo}-${f.id}`),
      );
      if (todasSeleccionadas) {
        return prev.filter((f) => !clavesPagina.has(`${f.tipo}-${f.id}`));
      }
      const existentes = new Set(prev.map((f) => `${f.tipo}-${f.id}`));
      return [
        ...prev,
        ...puedenPagarse.filter((f) => !existentes.has(`${f.tipo}-${f.id}`)),
      ];
    });
  };

  const todasSeleccionadas =
    puedenPagarse.length > 0 && puedenPagarse.every(estaSeleccionada);

  const algunaSeleccionada =
    puedenPagarse.some(estaSeleccionada) && !todasSeleccionadas;

  const handlePagar = () => {
    if (seleccionadas.length === 0) return;

    const facturasNormalizadas = seleccionadas.map((f) => ({
      tipoFactura: f.tipo, // "PLAN" | "TAREA"
      idFactura: f.id,
      idTarea: f.tipo === "TAREA" ? f.refTarea : undefined,
      monto: Number(f.montoAPagar),
      sucursal: f.sucursal ?? "",
      folio: String(f.folio),
    }));

    if (seleccionadas.length === 1) {
      const factura = facturasNormalizadas[0];
      const sufijo = factura.tipoFactura === "PLAN" ? "0" : "1";
      navigate(`/dashboard/pago/${factura.idFactura}_${sufijo}`);
    } else {
      navigate("/dashboard/pago/multiple", {
        state: { facturas: facturasNormalizadas, montoTotal },
      });
    }
  };

  const handleLimpiarFiltros = () => {
    setSeleccionadas([]);
    setPage(0);
    limpiarFiltros();
  };

  const isNotFound = facturasFiltradas.length === 0;
  const emptyRows =
    page > 0 && facturasFiltradas.length > 0
      ? Math.max(0, rowsPerPage - facturasFiltradas.length)
      : 0;

  // Estado de carga
  if (isLoading) {
    return (
      <Container>
        <Card style={{ marginTop: 24 }}>
          <TableSkeleton
            rows={6}
            headers={[
              "Tipo",
              "Folio",
              "Sociedad",
              "Glosa",
              "Fecha Facturación",
              "Periodo",
              "Monto",
              "Estado",
              "Fecha Pago",
            ]}
            showActions
          />
        </Card>
      </Container>
    );
  }

  // Estado de error
  if (isError) {
    return (
      <Container>
        <Box style={{ marginTop: 24 }}>
          <Alert severity={isForbidden ? "warning" : "error"}>
            {isForbidden
              ? "No tienes acceso a las facturas de la sociedad solicitada."
              : "Error al cargar las facturas. Por favor, intenta nuevamente."}
          </Alert>
        </Box>
      </Container>
    );
  }

  // Render normal con datos
  return (
    <Container>
      <Card style={{ marginTop: 24 }}>
        <Box style={{ padding: "16px 16px 0" }}>
          <Box style={{ maxWidth: isMobile ? "100%" : 560 }}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="selector-sociedad-label" shrink>
                Visualizando facturas de
              </InputLabel>
              <Select
                labelId="selector-sociedad-label"
                value={rutSociedad ?? ""}
                displayEmpty
                renderValue={(value) => {
                  if (!value) return "Todas las sociedades";
                  const sociedad = sociedades.find(
                    (item) => item.rutSociedad === value,
                  );
                  if (!sociedad) return value;
                  return `${sociedad.nombreSociedad || "Sociedad sin nombre"} — ${sociedad.rutSociedad}`;
                }}
                onChange={(event) => {
                  setRutSociedad(event.target.value);
                  setSeleccionadas([]);
                  setPage(0);
                }}
                label="Visualizando facturas de"
              >
                <MenuItem value="">Todas las sociedades</MenuItem>
                {sociedades.map((sociedad) => (
                  <MenuItem
                    key={sociedad.rutSociedad}
                    value={sociedad.rutSociedad}
                  >
                    {sociedad.nombreSociedad || "Sociedad sin nombre"} —{" "}
                    {sociedad.rutSociedad}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: "block", marginTop: 4, marginLeft: 2 }}
            >
              {sociedades.length} sociedad
              {sociedades.length !== 1 ? "es" : ""} autorizada
              {sociedades.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Box>

        {/* Filtro rápido por tipo */}
        <Box
          display="flex"
          alignItems="center"
          style={{ padding: "12px 16px 0", gap: 8 }}
        >
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginRight: 4 }}
          >
            Tipo:
          </Typography>
          {[
            { value: "TODOS", label: "Todos" },
            { value: "PLAN", label: "Planes" },
            { value: "TAREA", label: "Tareas" },
          ].map((opcion) => (
            <Chip
              key={opcion.value}
              label={opcion.label}
              color={
                (filtros.tipoFactura || "TODOS") === opcion.value
                  ? "primary"
                  : "default"
              }
              onClick={() =>
                updateFiltro(
                  "tipoFactura",
                  opcion.value === "TODOS" ? undefined : opcion.value,
                )
              }
              style={{ cursor: "pointer" }}
            />
          ))}
        </Box>

        <ClienteFilterPanel
          tipo="facturas"
          filtros={filtros}
          updateFiltro={updateFiltro}
          toggleEstado={toggleEstado}
          limpiarFiltros={handleLimpiarFiltros}
          removerFiltro={removerFiltro}
          filtrosActivos={filtrosActivos}
          hayFiltrosActivos={hayFiltrosActivos}
          searchPlaceholder="Buscar por folio o nombre de tarea..."
        />

        {/* Barra de selección múltiple */}
        {seleccionadas.length > 0 && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              padding: "10px 16px",
              backgroundColor: "#e3f2fd",
              borderTop: "1px solid #90caf9",
              borderBottom: "1px solid #90caf9",
            }}
          >
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Checkbox
                indeterminate={algunaSeleccionada}
                checked={todasSeleccionadas}
                onChange={handleToggleAll}
                color="primary"
                size="small"
              />
              <Typography variant="body2" style={{ color: "#1565c0" }}>
                <strong>{seleccionadas.length}</strong> factura
                {seleccionadas.length !== 1 ? "s" : ""} seleccionada
                {seleccionadas.length !== 1 ? "s" : ""} —{" "}
                <strong>{formatAsCLP(montoTotal)}</strong>
              </Typography>
            </Box>
            <Box display="flex" style={{ gap: 8 }}>
              <Button
                size="small"
                onClick={() => setSeleccionadas([])}
                style={{ color: "#1565c0" }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PaymentIcon />}
                onClick={handlePagar}
              >
                {seleccionadas.length === 1
                  ? "Pagar factura"
                  : `Pagar ${seleccionadas.length} facturas`}
              </Button>
            </Box>
          </Box>
        )}

        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          style={{ minHeight: 28, padding: "0 16px" }}
        >
          {isFetching && (
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary">
                Actualizando facturas…
              </Typography>
            </Box>
          )}
        </Box>

        <Scrollbar style={{ opacity: isFetching ? 0.65 : 1 }}>
          <TableContainer style={{ minWidth: isMobile ? "auto" : 900 }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TablaHead
                order={order}
                orderBy={orderBy}
                headLabel={visibleHeads}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {facturasFiltradas.map((factura) => (
                  <FacturaRow
                    key={`${factura.tipo}-${factura.id}`}
                    factura={factura}
                    tipo={factura.tipo === "PLAN" ? "planes" : "tareas"}
                    seleccionada={estaSeleccionada(factura)}
                    onToggle={handleToggle}
                    isMobile={isMobile}
                  />
                ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={visibleHeads.length} />
                  </TableRow>
                )}
              </TableBody>

              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={visibleHeads.length}
                      style={{ paddingTop: 24, paddingBottom: 24 }}
                    >
                      <EmptyState
                        icon={ReceiptOutlined}
                        title="No se encontraron facturas"
                        message={
                          hayFiltrosActivos
                            ? "Intenta ajustar los filtros para ver más resultados"
                            : "No tienes facturas registradas"
                        }
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={paginacion?.total ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />

        {/* Resumen de totales */}
        {facturasFiltradas.length > 0 && (
          <Box
            style={{
              padding: 24,
              backgroundColor: "#f9fafb",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={isMobile ? 2.5 : 3}
              justifyContent="space-around"
            >
              <Box style={{ textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Facturas Pagadas
                </Typography>
                <Typography
                  variant="h6"
                  style={{ color: "#22c55e", fontWeight: 600 }}
                >
                  {formatAsCLP(totales.totalPagado)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {totales.cantidadPagadas} factura
                  {totales.cantidadPagadas !== 1 ? "s" : ""}
                </Typography>
              </Box>

              <Box style={{ textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Facturas Pendientes
                </Typography>
                <Typography
                  variant="h6"
                  style={{ color: "#d97706", fontWeight: 600 }}
                >
                  {formatAsCLP(totales.totalPendiente)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {totales.cantidadPendientes} factura
                  {totales.cantidadPendientes !== 1 ? "s" : ""}
                </Typography>
              </Box>

              <Box style={{ textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Otros estados
                </Typography>
                <Typography
                  variant="h6"
                  style={{ color: "#64748b", fontWeight: 600 }}
                >
                  {formatAsCLP(totales.totalOtros)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {totales.cantidadOtros} factura
                  {totales.cantidadOtros !== 1 ? "s" : ""}
                </Typography>
              </Box>

              <Box style={{ textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Total General
                </Typography>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  {formatAsCLP(totales.totalGeneral)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {totales.cantidadTotal} factura
                  {totales.cantidadTotal !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
        {/* Toggle para facturas anuladas */}
        <Box
          display="flex"
          justifyContent="center"
          style={{ padding: "8px 16px 12px" }}
        >
          <Button
            size="small"
            style={{ color: "#9e9e9e", fontSize: "0.75rem" }}
            onClick={() => setMostrarAnuladas((v) => !v)}
          >
            {mostrarAnuladas
              ? "Ocultar facturas anuladas"
              : "Mostrar facturas anuladas"}
          </Button>
        </Box>
      </Card>
    </Container>
  );
}
